import axios from 'axios';
import { Timestamp } from 'firebase/firestore';
import { authService } from '../../features/auth/services/authService';
import { isProfanityError, useProfanityStore } from './profanity.service';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add authentication token to requests
api.interceptors.request.use(
  (config) => {
    const token = authService.getTokenSync();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle API response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const currentToken = authService.getTokenSync();
      console.log('[API] 401 intercepted', {
        url: originalRequest.url,
        hasToken: !!currentToken,
      });

      if (!currentToken) {
        console.log('[API] No token in store — skipping refresh, rejecting silently');
        return Promise.reject(error);
      }

      try {
        console.log('[API] Attempting token refresh...');
        const newToken = await authService.refreshAuthToken();
        console.log('[API] Token refreshed — retrying request');
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        console.error('[API] Token refresh failed — calling handleLogout', refreshError);
        await authService.handleLogout();
        window.location.href = '/signin';
        return Promise.reject(refreshError);
      }
    }

    // Check for profanity errors
    if (isProfanityError(error)) {
      setTimeout(() => {
        useProfanityStore.getState().openProfanityModal();
      }, 0);
      return Promise.reject(error);
    }

    // Handle network or other errors
    return Promise.reject(error);
  }
);

// Transform Firebase Timestamps in responses
api.interceptors.response.use((response) => {
  const transformTimestamp = (obj: any) => {
    for (const key in obj) {
      const value = obj[key];

      // Check if value is a Firebase Timestamp-like object
      if (value && typeof value === 'object' && '_seconds' in value && '_nanoseconds' in value) {
        obj[key] = new Timestamp(value._seconds, value._nanoseconds);
      }
      // Recursively transform nested objects
      else if (value && typeof value === 'object') {
        transformTimestamp(value);
      }
    }
    return obj;
  };

  response.data = transformTimestamp(response.data);
  return response;
});

// Auth endpoints
export const authEndpoints = {
  login: '/auth/login',
  register: '/auth/register',
  refreshToken: '/auth/refresh-token',
  verifyEmail: '/auth/verify-email',
  resetPassword: '/auth/reset-password',
  updateProfile: '/auth/profile',
} as const;

// User endpoints
export const userEndpoints = {
  profile: '/users/profile',
  updateProfile: '/users/profile',
  getById: (userId: string) => `/users/${userId}`,
} as const;

// Community endpoints
export const communityEndpoints = {
  getPosts: '/community/posts',
  getPost: (postId: string) => `/community/posts/${postId}`,
  createPost: '/community/posts',
  updatePost: (postId: string) => `/community/posts/${postId}`,
  deletePost: (postId: string) => `/community/posts/${postId}`,
  likePost: (postId: string) => `/community/posts/${postId}/like`,
  unlikePost: (postId: string) => `/community/posts/${postId}/unlike`,
  getComments: (postId: string) => `/community/posts/${postId}/comments`,
  addComment: (postId: string) => `/community/posts/${postId}/comments`,
} as const;

export default api;
